import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { resolveDailyRevealBrandingStyle } from '@low6-pickem/style-config';
import {
  isTeamAssetFolderId,
  setLeagueIp,
  setTeamAssetFolder,
  type TeamAssetFolderId,
} from '@/utils/teamAssetFolder';

export type DailyRevealStyleContextValue = {
  mainHeaderLogoUrl: string;
  revealBackgroundUrl: string;
  /**
   * False while the contest sport is still unknown (details request in flight). Consumers defer
   * rendering branded assets until this is true so the wrong league's logo/background never paints
   * (DKPLAT-813).
   */
  isSportResolved: boolean;
  /** Contest picks-core team image folder when set on style_config. */
  teamAssetFolder: TeamAssetFolderId | null;
};

const DailyRevealStyleContext = createContext<DailyRevealStyleContextValue | null>(null);

function readTeamAssetFolder(
  styleConfig?: Record<string, unknown> | null
): TeamAssetFolderId | null {
  const raw = styleConfig?.teamAssetFolder;
  return isTeamAssetFolderId(raw) ? raw : null;
}

export function DailyRevealStyleProvider({
  styleConfig,
  sport,
  isSportResolved,
  leagueIp = false,
  children,
}: {
  styleConfig?: Record<string, unknown> | null;
  sport?: string | null;
  /**
   * Optional override for the resolved flag. Pass `true` when the caller knows the sport will
   * never arrive (e.g. no contest id at all), so consumers fall through to today's defaults
   * rather than reserving an empty logo box forever.
   */
  isSportResolved?: boolean;
  leagueIp?: boolean;
  children: ReactNode;
}) {
  const teamAssetFolder = readTeamAssetFolder(styleConfig);

  // Sync ambient folder + League IP for image helpers (teamLogo / jerseyAvatar).
  // Set during render so async card mapping in the same tick sees the folder.
  setTeamAssetFolder(teamAssetFolder);
  setLeagueIp(leagueIp === true);

  useEffect(() => {
    return () => {
      setTeamAssetFolder(null);
      setLeagueIp(false);
    };
  }, []);

  const value = useMemo(() => {
    const branding = resolveDailyRevealBrandingStyle(styleConfig, sport);
    return {
      ...branding,
      isSportResolved: isSportResolved ?? branding.isSportResolved,
      teamAssetFolder,
    };
  }, [styleConfig, sport, isSportResolved, teamAssetFolder]);

  return (
    <DailyRevealStyleContext.Provider value={value}>{children}</DailyRevealStyleContext.Provider>
  );
}

export function useDailyRevealStyle(): DailyRevealStyleContextValue {
  const context = useContext(DailyRevealStyleContext);
  if (context) return context;
  const branding = resolveDailyRevealBrandingStyle(null, null);
  // "No provider" is the dev/debug render path (e.g. DebugPickCutoffView), NOT a loading state —
  // report it as resolved so those screens keep showing today's default logo instead of an empty
  // reserved box. Do not "fix" this to false.
  return { ...branding, isSportResolved: true, teamAssetFolder: null };
}
